import React from "react";

/**
 * Radial (speedometer) mood gauge driven by the counts of positive / negative
 * / neutral news items in the current view. Renders a semi-circular gauge
 * with a red→amber→green sweep and a needle that swings based on the
 * (positive - negative) delta over total sentiment volume.
 *
 * The description below the meter is derived purely from the sentiment
 * distribution — no market prices are consulted.
 */
export default function MoodMeter({ positive = 0, negative = 0, neutral = 0, testId = "mood-meter" }) {
    // Score: -1 (all negative) → +1 (all positive); neutral stories are ignored.
    const directional = positive + negative;
    const score = directional > 0 ? (positive - negative) / directional : 0;
    const angle = score * 90;
    const { label, tone, blurb } = describeMood(score, positive, negative, neutral);

    // Gauge geometry
    const W = 220, H = 130;
    const cx = W / 2, cy = H - 10, r = 90;

    return (
        <div className="w-full flex flex-col items-center" data-testid={testId}>
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
                {/* Colour arc segments — red / amber / green */}
                <path d={arc(cx, cy, r, -90, -NEUTRAL_DEG)} stroke="#ef4444" strokeWidth="14" fill="none" strokeLinecap="round" />
                <path d={arc(cx, cy, r, -NEUTRAL_DEG, NEUTRAL_DEG)}  stroke="#f59e0b" strokeWidth="14" fill="none" strokeLinecap="round" />
                <path d={arc(cx, cy, r, NEUTRAL_DEG, 90)}   stroke="#22c55e" strokeWidth="14" fill="none" strokeLinecap="round" />

                {/* Tick marks */}
                {[-90, -45, -NEUTRAL_DEG, 0, NEUTRAL_DEG, 45, 90].map((a) => {
                    const p1 = polar(cx, cy, r - 4, a);
                    const p2 = polar(cx, cy, r + 4, a);
                    return <line key={a} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />;
                })}

                {/* Needle */}
                <g style={{ transition: "transform 900ms cubic-bezier(.68,-0.55,.27,1.55)", transformOrigin: `${cx}px ${cy}px`, transform: `rotate(${angle}deg)` }}>
                    <line x1={cx} y1={cy} x2={cx} y2={cy - r + 10} stroke="#0F172A" strokeWidth="3" strokeLinecap="round" />
                    <circle cx={cx} cy={cy} r="6" fill="#0F172A" />
                    <circle cx={cx} cy={cy} r="2.5" fill="#22E578" />
                </g>

                {/* Endpoint labels */}
                <text x={cx - r + 4} y={cy + 12} className="font-sub" fontSize="9" letterSpacing="1.2" fill="#dc2626" fontWeight="700">NEGATIVE</text>
                <text x={cx + r - 4} y={cy + 12} textAnchor="end" className="font-sub" fontSize="9" letterSpacing="1.2" fill="#16a34a" fontWeight="700">POSITIVE</text>
            </svg>

            {/* Mood label + description */}
            <div className="text-center mt-1" data-testid={`${testId}-label`}>
                <div className={`font-heading font-black text-lg ${tone}`}>{label}</div>
                <div className="mt-2 font-body text-xs text-black/60 leading-relaxed max-w-[280px]">
                    {blurb}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function polar(cx, cy, r, degrees) {
    const rad = (degrees - 90) * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function arc(cx, cy, r, startDeg, endDeg) {
    const s = polar(cx, cy, r, startDeg);
    const e = polar(cx, cy, r, endDeg);
    const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
}
// |score| below this counts as Neutral (0.2 → 60:40 split of directional news).
const NEUTRAL_BAND = 0.2;
const NEUTRAL_DEG = NEUTRAL_BAND * 90;

function describeMood(score, pos, neg, neu) {
    const dir = pos + neg;
    if (dir === 0) {
        return { label: "Neutral", tone: "text-amber-600",
            blurb: neu ? `All ${neu} stories are neutral — no positive or negative signal yet.` : "Waiting for fresh news to compute the mood." };
    }
    const posPct = Math.round((pos / dir) * 100);
    const negPct = 100 - posPct;
    const strength = Math.abs(score) >= 0.6 ? "Strongly" : Math.abs(score) >= 0.35 ? "Clearly" : "Slightly";
    const thin = dir < 5 ? " Few stories so far, so the reading may shift." : "";
    const neuNote = neu ? `; ${neu} neutral not counted` : "";
    if (score >= NEUTRAL_BAND) {
        return { label: "Positive", tone: "text-emerald-600",
            blurb: `${strength} positive — ${posPct}% of the news with a clear direction is good news (${pos} positive vs ${neg} negative${neuNote}).${thin}` };
    }
    if (score <= -NEUTRAL_BAND) {
        return { label: "Negative", tone: "text-rose-600",
            blurb: `${strength} negative — ${negPct}% of the news with a clear direction is bad news (${neg} negative vs ${pos} positive${neuNote}).${thin}` };
    }
    return { label: "Neutral", tone: "text-amber-600",
        blurb: `Evenly split — ${pos} positive vs ${neg} negative (${posPct}:${negPct}${neuNote}). No side has a clear edge.${thin}` };
}
